import { getClient } from './client'
import { useUIStore } from '../store/ui-store'

let notificationsEnabled = false

export function requestNotificationPermission() {
  if (!('Notification' in window)) return
  if (Notification.permission === 'granted') {
    notificationsEnabled = true
    return
  }
  if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((perm) => {
      notificationsEnabled = perm === 'granted'
    })
  }
}

export function showNotification(title: string, body: string, roomId?: string) {
  if (!notificationsEnabled) return
  if (document.hasFocus()) return

  const notification = new Notification(title, {
    body,
    icon: '/favicon.svg',
    tag: roomId ?? 'chatpulse',
  })

  notification.onclick = () => {
    window.focus()
    if (roomId) {
      window.location.hash = `/room/${encodeURIComponent(roomId)}`
    }
    notification.close()
  }
}

const profileNameCache = new Map<string, string>()
const localpart = (id: string) => id.replace(/@(.+?):.*/, '$1')

// Resolve a sender's display name for the notification. The room member is often
// not loaded for a background room (lazy member loading), so fall back to a
// (cached) profile fetch before showing the raw user id.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function resolveSenderName(client: any, room: any, sender: string): Promise<string> {
  const member = room.getMember(sender)?.name
  if (member && member !== sender) return member
  if (profileNameCache.has(sender)) return profileNameCache.get(sender)!
  try {
    const profile = await client.getProfileInfo(sender)
    if (profile?.displayname) {
      profileNameCache.set(sender, profile.displayname)
      return profile.displayname
    }
  } catch { /* ignore — fall back below */ }
  return localpart(sender)
}

export function setupNotificationListener() {
  requestNotificationPermission()

  const client = getClient()
  if (!client) return

  client.on('Room.timeline' as any, async (event: any, room: any) => {
    // Skip events replayed during the initial sync (historical backfill before
    // PREPARED fires). Only notify for genuinely new, live messages.
    if (!client.isInitialSyncComplete()) return

    if (!event || !room) return
    const myUserId = client.getUserId()
    const sender = event.getSender()
    if (sender === myUserId) return
    if (event.getType() !== 'm.room.message') return

    const body = event.getContent()?.body
    if (!body) return

    if (useUIStore.getState().mutedRooms.has(room.roomId)) return

    const senderName = sender ? await resolveSenderName(client, room, sender) : 'Someone'
    showNotification(
      `${senderName} in ${room.name}`,
      body,
      room.roomId
    )
  })
}
